/**
 * ParametersSecretsModal — self-contained modal for viewing/editing a space's
 * runtime parameters and secrets.  Used by both SpaceFolderGrid and SpaceObjects.
 */
import React, { useState, useEffect } from "react";
import {
  Modal, Spin, Button, Table, Input, message, Typography,
} from "antd";
import { CopyOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { fetchParametersSecrets, grantPrivileges } from "components/Drawer/drawerApi";
import { RDS_TYPE_DEFAULT } from "components/Drawer/constants";
import { fetchEditMetadata } from "components/AddOrEditSpace/AddOrEditSpaceApi";
import { encryptString } from "utils/encrypt";
import { getLoggedInUser } from "utils/utils";
import type { IEditSpacePayload } from "components/Drawer/types";
import { extractApiErrorMessages } from "helpers";

const { Text } = Typography;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ParamSpaceInfo {
  spaceName: string;
  regionName: string;
  bucketName: string;
  metaDataUsers?: string;
  file_system_type?: string;
  /** Whether the space needs a git repo — defaults to true. */
  is_git_repo_needed?: boolean;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  spaceInfo: ParamSpaceInfo | null;
  domainName: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

const ParametersSecretsModal: React.FC<Props> = ({
  visible,
  onClose,
  spaceInfo,
  domainName,
}) => {
  const msalInstance = useMsal();

  const [parameters, setParameters] = useState<Array<{ name: string; value: string; path?: string }>>([{ name: "", value: "" }]);
  const [secrets, setSecrets] = useState<Array<{ name: string; value: string; arn?: string }>>([{ name: "", value: "" }]);
  const [originalParameters, setOriginalParameters] = useState<Array<{ name: string; value: string; path?: string }>>([]);
  const [originalSecrets, setOriginalSecrets] = useState<Array<{ name: string; value: string; arn?: string }>>([]);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    const empty = [{ name: "", value: "" }];
    setParameters(empty);
    setSecrets(empty);
    setOriginalParameters(empty);
    setOriginalSecrets(empty);
  };

  // Load parameters/secrets when opened
  useEffect(() => {
    if (!visible || !spaceInfo) return;

    setLoading(true);
    fetchParametersSecrets(msalInstance, spaceInfo.spaceName, domainName)
      .then((data) => {
        if (data) {
          const paramData = data.parameters.length > 0
            ? data.parameters.map((p: any) => ({ name: p.name, value: p.value, path: p.path }))
            : [{ name: "", value: "" }];
          const secretData = data.secrets.length > 0
            ? data.secrets.map((s: any) => ({ name: s.name, value: s.value, arn: s.arn }))
            : [{ name: "", value: "" }];
          setParameters(paramData);
          setSecrets(secretData);
          setOriginalParameters(JSON.parse(JSON.stringify(paramData)));
          setOriginalSecrets(JSON.parse(JSON.stringify(secretData)));
        } else {
          reset();
        }
      })
      .catch(() => {
        message.error("Failed to load parameters and secrets");
        reset();
      })
      .finally(() => setLoading(false));
  }, [visible, spaceInfo?.spaceName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    reset();
    onClose();
  };

  // Build diff list of edited params/secrets for audit trail
  const getEditedParams = () => {
    const edited: Array<{ field: string; name: string }> = [];
    const validOrig = originalParameters.filter(p => p.name.trim() && p.value.trim());
    const validCurr = parameters.filter(p => p.name.trim() && p.value.trim());
    validOrig.forEach(orig => {
      const curr = validCurr.find(p => p.name === orig.name);
      if (!curr || curr.value !== orig.value) edited.push({ field: "parameter_name", name: orig.name });
    });
    validCurr.forEach(curr => {
      if (!validOrig.find(p => p.name === curr.name)) edited.push({ field: "parameter_name", name: curr.name });
    });
    const validOrigS = originalSecrets.filter(s => s.name.trim() && s.value.trim());
    const validCurrS = secrets.filter(s => s.name.trim() && s.value.trim());
    validOrigS.forEach(orig => {
      const curr = validCurrS.find(s => s.name === orig.name);
      if (!curr || curr.value !== orig.value) edited.push({ field: "secret_name", name: orig.name });
    });
    validCurrS.forEach(curr => {
      if (!validOrigS.find(s => s.name === curr.name)) edited.push({ field: "secret_name", name: curr.name });
    });
    return edited;
  };

  const handleSave = async () => {
    if (!spaceInfo) return;

    const validParams = parameters.filter(p => p.name.trim() && p.value.trim());
    const validSecrets = secrets.filter(s => s.name.trim() && s.value.trim());

    const partialParam = parameters.find(p => (p.name.trim() && !p.value.trim()) || (!p.name.trim() && p.value.trim()));
    const partialSecret = secrets.find(s => (s.name.trim() && !s.value.trim()) || (!s.name.trim() && s.value.trim()));
    if (partialParam || partialSecret) {
      message.error("Please fill both name and value for all entries, or leave both empty");
      return;
    }
    if (validParams.length === 0 && validSecrets.length === 0) {
      message.error("Please add at least one parameter and one secret");
      return;
    }
    if (validParams.length === 0 && validSecrets.length > 0) {
      message.error("Please add at least one parameter when adding secrets");
      return;
    }
    if (validParams.length > 0 && validSecrets.length === 0) {
      message.error("Please add at least one secret when adding parameters");
      return;
    }

    setLoading(true);
    try {
      const meta = await fetchEditMetadata(
        msalInstance,
        spaceInfo.spaceName,
        spaceInfo.spaceName,
        spaceInfo.regionName,
        domainName,
      );
      if (!meta || typeof meta === "string") throw new Error("Failed to fetch metadata");

      const loggedInUser = getLoggedInUser() || (meta as any).requested_for || "";

      const payload: IEditSpacePayload = {
        space_name: spaceInfo.spaceName,
        file_system: spaceInfo.file_system_type || (meta as any).file_system_type || "",
        metadata: {
          name: spaceInfo.spaceName,
          mapped_groups: JSON.stringify((meta as any).mapped_groups || []),
          classification: (meta as any).classification || "",
          secondary_data_domain: (meta as any).secondary_data_domain || "",
          mapped_users: JSON.stringify((meta as any).mapped_users || []),
          licensing_obligation: (meta as any).licensing_obligation || "",
          datasource: (meta as any).datasource || "",
          space_description: (meta as any).space_description || "",
          retention_period_type: (meta as any).retention_period_type || "",
          retention_period_value: (meta as any).retention_period_value || "",
          need_cloud_gpu_compute: (meta as any).need_cloud_gpu_compute || "",
          need_rds_storage: (meta as any).need_rds_storage || "",
          rds_storage_type: (meta as any).rds_storage_type || ((meta as any).need_rds_storage === "yes" ? RDS_TYPE_DEFAULT : ""),
        },
        mode: "Edit",
        is_git_repo_needed: spaceInfo.is_git_repo_needed ?? true,
      };

      if ((meta as any).rnd_project_id) {
        payload.metadata.rnd_project_id = (meta as any).rnd_project_id;
      } else if ((meta as any).rnd_study_id) {
        payload.metadata.rnd_study_id = (meta as any).rnd_study_id;
      }

      if (validParams.length > 0 && validSecrets.length > 0) {
        const encryptedParams = validParams.map(p => ({ name: p.name, value: encryptString(p.value) }));
        const encryptedSecrets = validSecrets.map(s => ({ name: s.name, value: encryptString(s.value) }));
        payload.client_id = JSON.stringify(encryptedParams);
        payload.client_secret = JSON.stringify(encryptedSecrets);
      }

      const edited = getEditedParams();
      if (edited.length > 0) (payload as any).editted_params = edited;

      const success = await grantPrivileges(msalInstance, payload, "edit");
      if (success) {
        message.success("Parameters and secrets updated successfully");
        handleClose();
      } else {
        message.error("Failed to update parameters and secrets");
      }
    } catch (err) {
      console.error("Error saving parameters/secrets:", err);
      extractApiErrorMessages(err, "Failed to update parameters and secrets").forEach(msg => message.error(msg));
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const paramColumns = [
    {
      title: "Name", dataIndex: "name", key: "name", width: "30%",
      render: (text: string, _: any, index: number) => (
        <Input value={text} placeholder="Parameter name" onChange={(e) => {
          const u = [...parameters]; u[index].name = e.target.value; setParameters(u);
        }} />
      ),
    },
    {
      title: "Value", dataIndex: "value", key: "value", width: "30%",
      render: (text: string, _: any, index: number) => (
        <Input value={text || ""} placeholder="Parameter value" onChange={(e) => {
          const u = [...parameters]; u[index].value = e.target.value; setParameters(u);
        }} />
      ),
    },
    {
      title: "Path", dataIndex: "path", key: "path", width: "25%",
      render: (text: string) => (
        <Input value={text || ""} readOnly placeholder="N/A" addonAfter={
          <Button type="text" icon={<CopyOutlined />} size="small" style={{ border: "none", color: "#1677ff" }}
            onClick={async () => {
              if (text) { await navigator.clipboard.writeText(text); message.success("Path copied!"); } else { message.warning("No path to copy"); }
            }} />
        } />
      ),
    },
    {
      title: "Actions", key: "actions", width: "15%",
      render: (_: any, __: any, index: number) => (
        <Button danger icon={<MinusCircleOutlined />} onClick={() => {
          if (parameters.length === 1) { message.warning("At least one parameter is required"); return; }
          Modal.confirm({
            title: "Delete Parameter", content: `Delete "${parameters[index].name}"?`,
            okText: "Delete", cancelText: "Cancel", okButtonProps: { danger: true },
            onOk: () => { setParameters(parameters.filter((_, i) => i !== index)); message.success("Parameter deleted"); },
          });
        }} />
      ),
    },
  ];

  const secretColumns = [
    {
      title: "Name", dataIndex: "name", key: "name", width: "30%",
      render: (text: string, _: any, index: number) => (
        <Input value={text} placeholder="Secret name" onChange={(e) => {
          const u = [...secrets]; u[index].name = e.target.value; setSecrets(u);
        }} />
      ),
    },
    {
      title: "Value", dataIndex: "value", key: "value", width: "30%",
      render: (text: string, _: any, index: number) => (
        <Input.Password value={text || ""} placeholder="Secret value" onChange={(e) => {
          const u = [...secrets]; u[index].value = e.target.value; setSecrets(u);
        }} />
      ),
    },
    {
      title: "ARN", dataIndex: "arn", key: "arn", width: "25%",
      render: (text: string) => (
        <Input value={text || ""} readOnly placeholder="N/A" addonAfter={
          <Button type="text" icon={<CopyOutlined />} size="small" style={{ border: "none", color: "#1677ff" }}
            onClick={async () => {
              if (text) { await navigator.clipboard.writeText(text); message.success("ARN copied!"); } else { message.warning("No ARN to copy"); }
            }} />
        } />
      ),
    },
    {
      title: "Actions", key: "actions", width: "15%",
      render: (_: any, __: any, index: number) => (
        <Button danger icon={<MinusCircleOutlined />} onClick={() => {
          if (secrets.length === 1) { message.warning("At least one secret is required"); return; }
          Modal.confirm({
            title: "Delete Secret", content: `Delete "${secrets[index].name}"?`,
            okText: "Delete", cancelText: "Cancel", okButtonProps: { danger: true },
            onOk: () => { setSecrets(secrets.filter((_, i) => i !== index)); message.success("Secret deleted"); },
          });
        }} />
      ),
    },
  ];

  return (
    <Modal
      title="Parameters and Secrets"
      open={visible}
      onCancel={handleClose}
      width={900}
      centered
      bodyStyle={{ height: 500, padding: "20px", overflow: "hidden" }}
      footer={[
        <Button key="cancel" onClick={handleClose}>Cancel</Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSave}>Save Changes</Button>,
      ]}
    >
      <Spin spinning={loading} tip="Loading parameters and secrets...">
        <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Parameters */}
          <div style={{ flex: 1, minHeight: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <Text strong style={{ fontSize: 16 }}>Parameters</Text>
              <Button type="primary" icon={<PlusOutlined />}
                onClick={() => setParameters([...parameters, { name: "", value: "" }])}>Add</Button>
            </div>
            <div style={{ height: "calc(100% - 48px)", border: "1px solid #d9d9d9", borderRadius: 6, overflow: "auto" }}>
              <Table
                dataSource={loading ? [] : parameters.map((p, i) => ({ ...p, key: i }))}
                columns={paramColumns} pagination={false} size="small"
                scroll={{ y: 200, scrollToFirstRowOnChange: false }}
                locale={{ emptyText: loading ? "Loading..." : "No parameters added. Click 'Add' to create one." }}
              />
            </div>
          </div>
          {/* Secrets */}
          <div style={{ flex: 1, minHeight: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <Text strong style={{ fontSize: 16 }}>Secrets</Text>
              <Button type="primary" icon={<PlusOutlined />}
                onClick={() => setSecrets([...secrets, { name: "", value: "" }])}>Add</Button>
            </div>
            <div style={{ height: "calc(100% - 48px)", border: "1px solid #d9d9d9", borderRadius: 6, overflow: "auto" }}>
              <Table
                dataSource={loading ? [] : secrets.map((s, i) => ({ ...s, key: i }))}
                columns={secretColumns} pagination={false} size="small"
                scroll={{ y: 200, scrollToFirstRowOnChange: false }}
                locale={{ emptyText: loading ? "Loading..." : "No secrets added. Click 'Add' to create one." }}
              />
            </div>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default ParametersSecretsModal;
